import { useState } from "react";
import { supabase } from "@/lib/supabase";
import type { ClubPaymentSettingsFull } from "@/api/clubsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";

interface StripeConnectPanelProps {
  clubId: string;
  paymentSettings: ClubPaymentSettingsFull | null;
  onRefresh: () => void;
}

export function StripeConnectPanel({
  clubId,
  paymentSettings,
  onRefresh,
}: StripeConnectPanelProps) {
  const [loading, setLoading] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const hasStripeAccount = !!paymentSettings?.stripe_account_id;

  const handleSetupAccount = async () => {
    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/clubs/${clubId}?connect=success`;
      const refreshUrl = `${window.location.origin}/clubs/${clubId}?connect=refresh`;

      const { data, error } = await supabase.functions.invoke(
        "setup-club-stripe-account",
        {
          body: {
            clubId,
            returnUrl,
            refreshUrl,
          },
        }
      );

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Failed to set up Stripe account:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-stripe-login-link",
        {
          body: { clubId },
        }
      );

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Failed to create dashboard link:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke(
        "disconnect-club-stripe-account",
        {
          body: { clubId },
        }
      );

      if (error) throw error;
      setShowDisconnectConfirm(false);
      onRefresh();
    } catch (err) {
      console.error("Failed to disconnect Stripe account:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    if (
      paymentSettings?.stripe_account_status === "active" ||
      paymentSettings?.stripe_account_charges_enabled
    ) {
      return "bg-green-500";
    }
    if (paymentSettings?.stripe_account_status === "pending") {
      return "bg-yellow-500";
    }
    return "bg-red-500";
  };

  const getStatusText = () => {
    if (
      paymentSettings?.stripe_account_status === "active" ||
      paymentSettings?.stripe_account_charges_enabled
    ) {
      return "Active";
    }
    if (paymentSettings?.stripe_account_status === "pending") {
      return "Pending";
    }
    return "Incomplete";
  };

  const getStatusDescription = () => {
    if (paymentSettings?.stripe_account_status === "pending") {
      return "It may take a few minutes for Stripe to verify your account information.";
    }
    if (paymentSettings?.stripe_account_status === "incomplete") {
      return "You'll need to update this account with Stripe before accepting payments.";
    }
    return null;
  };

  const businessName =
    paymentSettings?.stripe_business_name || "Club payment account";
  const last4 = paymentSettings?.stripe_account_last4;
  const statusDescription = hasStripeAccount ? getStatusDescription() : null;

  return (
    <div className="bg-surface rounded-2xl p-4">
      <h3 className="text-body-1-medium text-text-primary mb-4">
        Payout account
      </h3>

      {!hasStripeAccount ? (
        <div>
          <button
            onClick={handleSetupAccount}
            disabled={loading}
            className="w-full h-12 bg-text-primary text-background rounded-full text-body-1-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Setting up..." : "Set up payment details"}
          </button>
          <p className="text-body-3 text-text-secondary mt-3">
            Add your banking details to start accepting membership fees and
            one-time payments.
          </p>
        </div>
      ) : (
        <div>
          <button
            onClick={handleSetupAccount}
            disabled={loading}
            className="w-full bg-background rounded-xl p-4 border border-border-tertiary hover:border-border-secondary transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-body-2 text-text-primary font-medium truncate">
                  {businessName}
                </p>
                {last4 && (
                  <p className="text-body-3 text-text-secondary mt-0.5">
                    &bull;&bull;&bull;&bull; {last4}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <span className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                <span className="text-body-3 text-text-secondary">
                  {getStatusText()}
                </span>
              </div>
            </div>
          </button>

          {statusDescription && (
            <p className="text-body-3 text-text-secondary mt-2">
              {statusDescription}
            </p>
          )}

          <div className="flex items-center justify-between mt-3">
            <button
              onClick={handleViewDashboard}
              disabled={loading}
              className="text-body-2 text-text-secondary hover:text-text-primary transition-colors inline-flex items-center gap-1.5"
            >
              View subscription dashboard
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className="text-text-secondary"
              >
                <path
                  d="M3.5 1.5H10.5V8.5M10.5 1.5L1.5 10.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              onClick={() => setShowDisconnectConfirm(true)}
              className="text-body-3 text-text-tertiary hover:text-accent-negative transition-colors"
            >
              Disconnect account
            </button>
          </div>
        </div>
      )}

      <p className="text-body-3 text-text-tertiary text-right mt-4">
        Powered by Stripe
      </p>

      <AlertDialog
        open={showDisconnectConfirm}
        onOpenChange={setShowDisconnectConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect &amp; cancel subscriptions?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the payment account from your club and cancel all
              pending subscriptions. You&apos;ll need to set up a new account to
              accept payments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-red-500 hover:bg-red-600"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
